import { useMemo } from "react";
import { Palette } from "lucide-react";
import type { Furniture, MaterialId } from "../../lib/types";
import { COUNTERTOP_MATERIAL_IDS, MATERIALS, materialInfo } from "../../lib/furniture/materials";
import { resolveMaterial } from "../../lib/furniture/resolve";
import { FurniturePreview } from "../FurniturePreview";
import { useDesign } from "../../lib/store";
import { cn } from "../../lib/cn";
import { track } from "../../lib/telemetry";
import { InspectorSection } from "./InspectorSection";

interface Props {
  selected: Furniture;
  onUpdate: (patch: Partial<Furniture>) => void;
  /** Start folded behind its header (mobile inspector). */
  collapsible?: boolean;
}

/**
 * Material picker. Each material swatch is the *selected piece itself*
 * rendered in that material, so the user sees exactly what they'd get
 * rather than an abstract chip. Below it, tint swatches curated for the
 * active material plus a free color input.
 */
export function FurnitureMaterialSection({ selected, onUpdate, collapsible = false }: Props) {
  const material = useMemo(() => resolveMaterial(selected), [selected]);
  const info = materialInfo(material.id);

  function setMaterial(id: MaterialId) {
    if (id === material.id) return;
    // Snap the tint to the new material's first swatch when the current tint
    // is one of the *old* material's curated colors — a leather sofa turned
    // to marble shouldn't stay saddle brown. Custom tints are preserved.
    const oldSwatches = materialInfo(material.id).swatches;
    const nextInfo = materialInfo(id);
    const tint = oldSwatches.includes(material.tint) ? nextInfo.swatches[0] : material.tint;
    onUpdate({ material: { id, tint }, color: tint });
    track("material_changed", { material: id });
  }

  function setTint(tint: string) {
    onUpdate({ material: { id: material.id, tint }, color: tint });
  }

  const general = MATERIALS.filter((m) => !COUNTERTOP_MATERIAL_IDS.has(m.id));
  const counters = MATERIALS.filter((m) => COUNTERTOP_MATERIAL_IDS.has(m.id));

  function renderChoice(m: (typeof MATERIALS)[number]) {
    const active = m.id === material.id;
    const tint = active ? material.tint : m.swatches[0];
    return (
      <button
        key={m.id}
        type="button"
        role="radio"
        aria-checked={active}
        aria-label={m.label}
        title={m.label}
        onClick={() => setMaterial(m.id)}
        className={cn(
          "group flex flex-col items-center gap-1 rounded-xl p-1.5 ring-1 transition-all",
          active
            ? "bg-white ring-2 ring-accent-500 shadow-sm"
            : "bg-ink-50 ring-ink-100 hover:bg-white hover:ring-ink-300 hover:-translate-y-px"
        )}
      >
        <FurniturePreview
          furniture={{ ...selected, color: tint, material: { id: m.id, tint } }}
          size={44}
          pad={3}
          lod={1}
          textures={false}
          shadow={false}
        />
        <span className="line-clamp-2 text-center text-caption leading-tight text-ink-500">{m.label}</span>
      </button>
    );
  }

  return (
    <InspectorSection
      testId="furniture-material-section"
      icon={<Palette className="size-3.5" />}
      label="Material"
      summary={info.label}
      collapsible={collapsible}
      defaultOpen={!collapsible}
    >
      <div role="radiogroup" aria-label="Material">
        <div className="grid grid-cols-4 gap-1.5">{general.map(renderChoice)}</div>
        <div className="mb-1.5 mt-3 text-caption uppercase tracking-wide font-semibold text-ink-400">
          Countertops
        </div>
        <div className="grid grid-cols-4 gap-1.5">{counters.map(renderChoice)}</div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {info.swatches.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={`Tint ${c}`}
            aria-pressed={material.tint === c}
            onClick={() => setTint(c)}
            className={cn(
              "size-7 rounded-full ring-1 ring-black/10 transition-transform",
              material.tint === c ? "ring-2 ring-accent-500 scale-110" : "hover:scale-105"
            )}
            style={{ background: c }}
          />
        ))}
        <label className="relative ml-1 inline-flex size-7 cursor-pointer items-center justify-center rounded-full ring-1 ring-ink-200 overflow-hidden">
          <input
            type="color"
            value={material.tint}
            onChange={(e) => setTint(e.target.value)}
            onFocus={() => useDesign.getState().beginGesture()}
            onBlur={() => useDesign.getState().endGesture()}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
            aria-label="Custom tint"
          />
          <span
            className="pointer-events-none size-4 rounded-full ring-1 ring-black/10"
            style={{ background: `conic-gradient(from 0deg, #f43f5e, #f59e0b, #22c55e, #2a70f5, #a855f7, #f43f5e)` }}
          />
        </label>
      </div>
    </InspectorSection>
  );
}
